import React, { useState, useEffect, useCallback } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, ReferenceLine } from 'recharts';
import { TrendingUp, Users, Brain, AlertCircle, CheckCircle, Target, BarChart3, Scale } from 'lucide-react';

const EnhancedResultsPage = ({ sessionId, onStartNew }) => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchResults = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/assessment/results/${sessionId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch results: ${response.status}`);
      }
      
      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // Cross-walk data for converting theta scores to sum scores on other measures
  const crossWalkData = {
    'LSAS': { name: 'Liebowitz Social Anxiety Scale', maxScore: 72, items: 24 },
    'SPIN': { name: 'Social Phobia Inventory', maxScore: 68, items: 17 },
    'SIAS-20': { name: 'Social Interaction Anxiety Scale (20-item)', maxScore: 80, items: 20 },
    'SPS-20': { name: 'Social Phobia Scale (20-item)', maxScore: 80, items: 20 },
    'DSMSAD': { name: 'DSM-5 Social Anxiety Disorder Scale', maxScore: 40, items: 10 },
    'SIAS-6': { name: 'Social Interaction Anxiety Scale (6-item)', maxScore: 24, items: 6 },
    'SPS-6': { name: 'Social Phobia Scale (6-item)', maxScore: 24, items: 6 },
    'GSAS-12': { name: 'Generalized Social Anxiety Scale (12-item)', maxScore: 48, items: 12 },
    'Mini-SPIN': { name: 'Mini Social Phobia Inventory', maxScore: 12, items: 3 },
    'SOPHS': { name: 'Short Online Phobia Screening', maxScore: 20, items: 5 }
  };

  // Cross-walk lookup tables (condensed for key ranges)
  const crossWalkTables = {
    'LSAS': [
      { theta: -1.95, score: 0 }, { theta: -1.57, score: 1 }, { theta: -1.36, score: 2 },
      { theta: -1.19, score: 3 }, { theta: -1.04, score: 4 }, { theta: -0.91, score: 5 },
      { theta: -0.80, score: 6 }, { theta: -0.70, score: 7 }, { theta: -0.61, score: 8 },
      { theta: -0.52, score: 9 }, { theta: -0.44, score: 10 }, { theta: -0.37, score: 11 },
      { theta: -0.30, score: 12 }, { theta: -0.23, score: 13 }, { theta: -0.17, score: 14 },
      { theta: -0.10, score: 15 }, { theta: -0.05, score: 16 }, { theta: 0.01, score: 17 },
      { theta: 0.07, score: 18 }, { theta: 0.12, score: 19 }, { theta: 0.18, score: 20 },
      { theta: 0.23, score: 21 }, { theta: 0.28, score: 22 }, { theta: 0.33, score: 23 },
      { theta: 0.38, score: 24 }, { theta: 0.43, score: 25 }, { theta: 0.48, score: 26 },
      { theta: 0.53, score: 27 }, { theta: 0.57, score: 28 }, { theta: 0.62, score: 29 },
      { theta: 0.67, score: 30 }, { theta: 0.71, score: 31 }, { theta: 0.76, score: 32 },
      { theta: 0.81, score: 33 }, { theta: 0.85, score: 34 }, { theta: 0.90, score: 35 },
      { theta: 0.95, score: 36 }, { theta: 0.99, score: 37 }, { theta: 1.04, score: 38 },
      { theta: 1.08, score: 39 }, { theta: 1.13, score: 40 }, { theta: 1.17, score: 41 },
      { theta: 1.22, score: 42 }, { theta: 1.27, score: 43 }, { theta: 1.31, score: 44 },
      { theta: 1.36, score: 45 }, { theta: 1.41, score: 46 }, { theta: 1.46, score: 47 },
      { theta: 1.50, score: 48 }, { theta: 1.55, score: 49 }, { theta: 1.60, score: 50 },
      { theta: 1.65, score: 51 }, { theta: 1.70, score: 52 }, { theta: 1.76, score: 53 },
      { theta: 1.81, score: 54 }, { theta: 1.86, score: 55 }, { theta: 1.92, score: 56 },
      { theta: 1.98, score: 57 }, { theta: 2.04, score: 58 }, { theta: 2.10, score: 59 },
      { theta: 2.16, score: 60 }, { theta: 2.23, score: 61 }, { theta: 2.30, score: 62 },
      { theta: 2.38, score: 63 }, { theta: 2.46, score: 64 }, { theta: 2.54, score: 65 },
      { theta: 2.63, score: 66 }, { theta: 2.73, score: 67 }, { theta: 2.85, score: 68 },
      { theta: 2.97, score: 69 }, { theta: 3.11, score: 70 }, { theta: 3.29, score: 71 },
      { theta: 3.54, score: 72 }
    ],
    'SPIN': [
      { theta: -1.76, score: 0 }, { theta: -1.34, score: 1 }, { theta: -1.12, score: 2 },
      { theta: -0.95, score: 3 }, { theta: -0.82, score: 4 }, { theta: -0.69, score: 5 },
      { theta: -0.58, score: 6 }, { theta: -0.48, score: 7 }, { theta: -0.39, score: 8 },
      { theta: -0.30, score: 9 }, { theta: -0.23, score: 10 }, { theta: -0.15, score: 11 },
      { theta: -0.08, score: 12 }, { theta: -0.02, score: 13 }, { theta: 0.05, score: 14 },
      { theta: 0.11, score: 15 }, { theta: 0.17, score: 16 }, { theta: 0.22, score: 17 },
      { theta: 0.28, score: 18 }, { theta: 0.33, score: 19 }, { theta: 0.38, score: 20 },
      { theta: 0.43, score: 21 }, { theta: 0.48, score: 22 }, { theta: 0.53, score: 23 },
      { theta: 0.58, score: 24 }, { theta: 0.63, score: 25 }, { theta: 0.68, score: 26 },
      { theta: 0.73, score: 27 }, { theta: 0.77, score: 28 }, { theta: 0.82, score: 29 },
      { theta: 0.87, score: 30 }, { theta: 0.91, score: 31 }, { theta: 0.96, score: 32 },
      { theta: 1.00, score: 33 }, { theta: 1.05, score: 34 }, { theta: 1.09, score: 35 },
      { theta: 1.14, score: 36 }, { theta: 1.18, score: 37 }, { theta: 1.23, score: 38 },
      { theta: 1.27, score: 39 }, { theta: 1.32, score: 40 }, { theta: 1.36, score: 41 },
      { theta: 1.41, score: 42 }, { theta: 1.46, score: 43 }, { theta: 1.50, score: 44 },
      { theta: 1.55, score: 45 }, { theta: 1.60, score: 46 }, { theta: 1.65, score: 47 },
      { theta: 1.70, score: 48 }, { theta: 1.75, score: 49 }, { theta: 1.80, score: 50 },
      { theta: 1.85, score: 51 }, { theta: 1.91, score: 52 }, { theta: 1.97, score: 53 },
      { theta: 2.02, score: 54 }, { theta: 2.08, score: 55 }, { theta: 2.15, score: 56 },
      { theta: 2.21, score: 57 }, { theta: 2.28, score: 58 }, { theta: 2.35, score: 59 },
      { theta: 2.43, score: 60 }, { theta: 2.51, score: 61 }, { theta: 2.60, score: 62 },
      { theta: 2.69, score: 63 }, { theta: 2.80, score: 64 }, { theta: 2.91, score: 65 },
      { theta: 3.05, score: 66 }, { theta: 3.22, score: 67 }, { theta: 3.47, score: 68 }
    ],
    'SIAS-20': [
      { theta: -2.27, score: 0 }, { theta: -1.98, score: 1 }, { theta: -1.77, score: 2 },
      { theta: -1.60, score: 3 }, { theta: -1.45, score: 4 }, { theta: -1.31, score: 5 },
      { theta: -1.18, score: 6 }, { theta: -1.06, score: 7 }, { theta: -0.95, score: 8 },
      { theta: -0.85, score: 9 }, { theta: -0.75, score: 10 }, { theta: -0.66, score: 11 },
      { theta: -0.58, score: 12 }, { theta: -0.50, score: 13 }, { theta: -0.43, score: 14 },
      { theta: -0.36, score: 15 }, { theta: -0.29, score: 16 }, { theta: -0.23, score: 17 },
      { theta: -0.17, score: 18 }, { theta: -0.11, score: 19 }, { theta: -0.06, score: 20 },
      { theta: 0.00, score: 21 }, { theta: 0.05, score: 22 }, { theta: 0.10, score: 23 },
      { theta: 0.15, score: 24 }, { theta: 0.20, score: 25 }, { theta: 0.25, score: 26 },
      { theta: 0.30, score: 27 }, { theta: 0.34, score: 28 }, { theta: 0.39, score: 29 },
      { theta: 0.43, score: 30 }, { theta: 0.48, score: 31 }, { theta: 0.52, score: 32 },
      { theta: 0.56, score: 33 }, { theta: 0.61, score: 34 }, { theta: 0.65, score: 35 },
      { theta: 0.69, score: 36 }, { theta: 0.73, score: 37 }, { theta: 0.77, score: 38 },
      { theta: 0.81, score: 39 }, { theta: 0.86, score: 40 }, { theta: 0.90, score: 41 },
      { theta: 0.94, score: 42 }, { theta: 0.98, score: 43 }, { theta: 1.02, score: 44 },
      { theta: 1.06, score: 45 }, { theta: 1.10, score: 46 }, { theta: 1.14, score: 47 },
      { theta: 1.18, score: 48 }, { theta: 1.23, score: 49 }, { theta: 1.27, score: 50 },
      { theta: 1.31, score: 51 }, { theta: 1.35, score: 52 }, { theta: 1.39, score: 53 },
      { theta: 1.44, score: 54 }, { theta: 1.48, score: 55 }, { theta: 1.53, score: 56 },
      { theta: 1.57, score: 57 }, { theta: 1.62, score: 58 }, { theta: 1.66, score: 59 },
      { theta: 1.71, score: 60 }, { theta: 1.76, score: 61 }, { theta: 1.81, score: 62 },
      { theta: 1.86, score: 63 }, { theta: 1.91, score: 64 }, { theta: 1.97, score: 65 },
      { theta: 2.03, score: 66 }, { theta: 2.08, score: 67 }, { theta: 2.15, score: 68 },
      { theta: 2.21, score: 69 }, { theta: 2.28, score: 70 }, { theta: 2.35, score: 71 },
      { theta: 2.43, score: 72 }, { theta: 2.51, score: 73 }, { theta: 2.60, score: 74 },
      { theta: 2.69, score: 75 }, { theta: 2.80, score: 76 }, { theta: 2.91, score: 77 },
      { theta: 3.05, score: 78 }, { theta: 3.21, score: 79 }, { theta: 3.45, score: 80 }
    ],
    'SPS-20': [
      { theta: -1.41, score: 0 }, { theta: -0.92, score: 1 }, { theta: -0.68, score: 2 },
      { theta: -0.52, score: 3 }, { theta: -0.38, score: 4 }, { theta: -0.25, score: 5 },
      { theta: -0.14, score: 6 }, { theta: -0.05, score: 7 }, { theta: 0.04, score: 8 },
      { theta: 0.11, score: 9 }, { theta: 0.19, score: 10 }, { theta: 0.25, score: 11 },
      { theta: 0.31, score: 12 }, { theta: 0.37, score: 13 }, { theta: 0.43, score: 14 },
      { theta: 0.48, score: 15 }, { theta: 0.53, score: 16 }, { theta: 0.58, score: 17 },
      { theta: 0.63, score: 18 }, { theta: 0.67, score: 19 }, { theta: 0.72, score: 20 },
      { theta: 0.76, score: 21 }, { theta: 0.80, score: 22 }, { theta: 0.85, score: 23 },
      { theta: 0.89, score: 24 }, { theta: 0.93, score: 25 }, { theta: 0.96, score: 26 },
      { theta: 1.00, score: 27 }, { theta: 1.04, score: 28 }, { theta: 1.08, score: 29 },
      { theta: 1.11, score: 30 }, { theta: 1.15, score: 31 }, { theta: 1.19, score: 32 },
      { theta: 1.22, score: 33 }, { theta: 1.26, score: 34 }, { theta: 1.29, score: 35 },
      { theta: 1.33, score: 36 }, { theta: 1.36, score: 37 }, { theta: 1.40, score: 38 },
      { theta: 1.43, score: 39 }, { theta: 1.47, score: 40 }, { theta: 1.50, score: 41 },
      { theta: 1.54, score: 42 }, { theta: 1.57, score: 43 }, { theta: 1.60, score: 44 },
      { theta: 1.64, score: 45 }, { theta: 1.67, score: 46 }, { theta: 1.71, score: 47 },
      { theta: 1.74, score: 48 }, { theta: 1.78, score: 49 }, { theta: 1.82, score: 50 },
      { theta: 1.85, score: 51 }, { theta: 1.89, score: 52 }, { theta: 1.92, score: 53 },
      { theta: 1.96, score: 54 }, { theta: 2.00, score: 55 }, { theta: 2.04, score: 56 },
      { theta: 2.08, score: 57 }, { theta: 2.12, score: 58 }, { theta: 2.16, score: 59 },
      { theta: 2.20, score: 60 }, { theta: 2.24, score: 61 }, { theta: 2.29, score: 62 },
      { theta: 2.33, score: 63 }, { theta: 2.38, score: 64 }, { theta: 2.43, score: 65 },
      { theta: 2.48, score: 66 }, { theta: 2.53, score: 67 }, { theta: 2.58, score: 68 },
      { theta: 2.64, score: 69 }, { theta: 2.70, score: 70 }, { theta: 2.76, score: 71 },
      { theta: 2.83, score: 72 }, { theta: 2.90, score: 73 }, { theta: 2.98, score: 74 },
      { theta: 3.06, score: 75 }, { theta: 3.15, score: 76 }, { theta: 3.26, score: 77 },
      { theta: 3.38, score: 78 }, { theta: 3.53, score: 79 }, { theta: 3.77, score: 80 }
    ],
    'DSMSAD': [
      { theta: -1.26, score: 0 }, { theta: -0.68, score: 1 }, { theta: -0.41, score: 2 },
      { theta: -0.23, score: 3 }, { theta: -0.08, score: 4 }, { theta: 0.06, score: 5 },
      { theta: 0.18, score: 6 }, { theta: 0.28, score: 7 }, { theta: 0.38, score: 8 },
      { theta: 0.47, score: 9 }, { theta: 0.56, score: 10 }, { theta: 0.64, score: 11 },
      { theta: 0.72, score: 12 }, { theta: 0.80, score: 13 }, { theta: 0.87, score: 14 },
      { theta: 0.95, score: 15 }, { theta: 1.02, score: 16 }, { theta: 1.08, score: 17 },
      { theta: 1.15, score: 18 }, { theta: 1.22, score: 19 }, { theta: 1.28, score: 20 },
      { theta: 1.35, score: 21 }, { theta: 1.41, score: 22 }, { theta: 1.48, score: 23 },
      { theta: 1.54, score: 24 }, { theta: 1.61, score: 25 }, { theta: 1.68, score: 26 },
      { theta: 1.74, score: 27 }, { theta: 1.81, score: 28 }, { theta: 1.89, score: 29 },
      { theta: 1.96, score: 30 }, { theta: 2.04, score: 31 }, { theta: 2.12, score: 32 },
      { theta: 2.20, score: 33 }, { theta: 2.29, score: 34 }, { theta: 2.39, score: 35 },
      { theta: 2.50, score: 36 }, { theta: 2.61, score: 37 }, { theta: 2.75, score: 38 },
      { theta: 2.94, score: 39 }, { theta: 3.25, score: 40 }
    ],
    'SIAS-6': [
      { theta: -1.28, score: 0 }, { theta: -0.78, score: 1 }, { theta: -0.49, score: 2 },
      { theta: -0.26, score: 3 }, { theta: -0.05, score: 4 }, { theta: 0.14, score: 5 },
      { theta: 0.31, score: 6 }, { theta: 0.47, score: 7 }, { theta: 0.62, score: 8 },
      { theta: 0.76, score: 9 }, { theta: 0.90, score: 10 }, { theta: 1.03, score: 11 },
      { theta: 1.16, score: 12 }, { theta: 1.29, score: 13 }, { theta: 1.42, score: 14 },
      { theta: 1.55, score: 15 }, { theta: 1.69, score: 16 }, { theta: 1.82, score: 17 },
      { theta: 1.96, score: 18 }, { theta: 2.11, score: 19 }, { theta: 2.26, score: 20 },
      { theta: 2.43, score: 21 }, { theta: 2.61, score: 22 }, { theta: 2.82, score: 23 },
      { theta: 3.10, score: 24 }
    ],
    'SPS-6': [
      { theta: -0.91, score: 0 }, { theta: -0.23, score: 1 }, { theta: 0.03, score: 2 },
      { theta: 0.21, score: 3 }, { theta: 0.37, score: 4 }, { theta: 0.53, score: 5 },
      { theta: 0.67, score: 6 }, { theta: 0.79, score: 7 }, { theta: 0.90, score: 8 },
      { theta: 1.01, score: 9 }, { theta: 1.11, score: 10 }, { theta: 1.21, score: 11 },
      { theta: 1.31, score: 12 }, { theta: 1.41, score: 13 }, { theta: 1.50, score: 14 },
      { theta: 1.60, score: 15 }, { theta: 1.70, score: 16 }, { theta: 1.81, score: 17 },
      { theta: 1.92, score: 18 }, { theta: 2.03, score: 19 }, { theta: 2.16, score: 20 },
      { theta: 2.29, score: 21 }, { theta: 2.44, score: 22 }, { theta: 2.64, score: 23 },
      { theta: 2.96, score: 24 }
    ],
    'GSAS-12': [
      { theta: -1.37, score: 0 }, { theta: -0.91, score: 1 }, { theta: -0.66, score: 2 },
      { theta: -0.46, score: 3 }, { theta: -0.30, score: 4 }, { theta: -0.14, score: 5 },
      { theta: -0.01, score: 6 }, { theta: 0.10, score: 7 }, { theta: 0.21, score: 8 },
      { theta: 0.30, score: 9 }, { theta: 0.39, score: 10 }, { theta: 0.48, score: 11 },
      { theta: 0.56, score: 12 }, { theta: 0.63, score: 13 }, { theta: 0.70, score: 14 },
      { theta: 0.77, score: 15 }, { theta: 0.84, score: 16 }, { theta: 0.90, score: 17 },
      { theta: 0.97, score: 18 }, { theta: 1.03, score: 19 }, { theta: 1.09, score: 20 },
      { theta: 1.15, score: 21 }, { theta: 1.21, score: 22 }, { theta: 1.27, score: 23 },
      { theta: 1.33, score: 24 }, { theta: 1.39, score: 25 }, { theta: 1.44, score: 26 },
      { theta: 1.50, score: 27 }, { theta: 1.56, score: 28 }, { theta: 1.62, score: 29 },
      { theta: 1.68, score: 30 }, { theta: 1.74, score: 31 }, { theta: 1.80, score: 32 },
      { theta: 1.86, score: 33 }, { theta: 1.93, score: 34 }, { theta: 1.99, score: 35 },
      { theta: 2.06, score: 36 }, { theta: 2.13, score: 37 }, { theta: 2.20, score: 38 },
      { theta: 2.28, score: 39 }, { theta: 2.36, score: 40 }, { theta: 2.44, score: 41 },
      { theta: 2.54, score: 42 }, { theta: 2.64, score: 43 }, { theta: 2.75, score: 44 },
      { theta: 2.86, score: 45 }, { theta: 3.00, score: 46 }, { theta: 3.17, score: 47 },
      { theta: 3.41, score: 48 }
    ],
    'Mini-SPIN': [
      { theta: -1.13, score: 0 }, { theta: -0.50, score: 1 }, { theta: -0.18, score: 2 },
      { theta: 0.05, score: 3 }, { theta: 0.26, score: 4 }, { theta: 0.47, score: 5 },
      { theta: 0.67, score: 6 }, { theta: 0.86, score: 7 }, { theta: 1.06, score: 8 },
      { theta: 1.26, score: 9 }, { theta: 1.49, score: 10 }, { theta: 1.76, score: 11 },
      { theta: 2.21, score: 12 }
    ],
    'SOPHS': [
      { theta: -1.01, score: 0 }, { theta: -0.30, score: 1 }, { theta: 0.00, score: 2 },
      { theta: 0.23, score: 3 }, { theta: 0.41, score: 4 }, { theta: 0.57, score: 5 },
      { theta: 0.71, score: 6 }, { theta: 0.85, score: 7 }, { theta: 0.98, score: 8 },
      { theta: 1.11, score: 9 }, { theta: 1.23, score: 10 }, { theta: 1.36, score: 11 },
      { theta: 1.49, score: 12 }, { theta: 1.62, score: 13 }, { theta: 1.75, score: 14 },
      { theta: 1.88, score: 15 }, { theta: 2.02, score: 16 }, { theta: 2.18, score: 17 },
      { theta: 2.36, score: 18 }, { theta: 2.57, score: 19 }, { theta: 2.93, score: 20 }
    ]
  };

  // Function to find closest theta match in cross-walk table
  const findCrossWalkScore = (userTheta, scaleName) => {
    const table = crossWalkTables[scaleName];
    if (!table) return null;

    // Find the closest theta value
    let closest = table[0];
    let minDiff = Math.abs(userTheta - closest.theta);

    for (const entry of table) {
      const diff = Math.abs(userTheta - entry.theta);
      if (diff < minDiff) {
        minDiff = diff;
        closest = entry;
      }
    }

    return closest;
  };

  // PDF Download function - MOVED TO CORRECT POSITION
  const downloadPDF = async () => {
    try {
      setPdfLoading(true);
      
      const patientInfo = {
        name: 'Assessment Participant',
        age: 'Not specified',
        gender: 'Not specified'
      };
      
      console.log('Requesting PDF for session:', sessionId);
      
      const response = await fetch('/api/assessment/report/comprehensive-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          patient_info: patientInfo
        })
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to generate PDF: ${response.status} - ${errorData}`);
      }
      
      const data = await response.json();
      console.log('PDF response received, size:', data.size_bytes);
      
      // Convert base64 to blob and download
      const byteCharacters = atob(data.pdf_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      console.log('PDF downloaded successfully:', data.filename);
      
    } catch (error) {
      console.error('PDF download failed:', error);
      alert(`Failed to generate PDF report: ${error.message}`);
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Analyzing your results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <h3 className="text-lg font-medium text-red-800">Error Loading Results</h3>
          </div>
          <p className="mt-2 text-red-700">{error}</p>
          <button
            onClick={onStartNew}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
          >
            Start New Assessment
          </button>
        </div>
      </div>
    );
  }

  const { assessment_summary, percentile_feedback, severity_assessment, distribution_visualization, predicted_responses } = results;

  const ScoreDistributionChart = () => {
    const data = distribution_visualization.distribution_curve.map(point => ({
      x: point.x,
      y: point.y,
      isInRange: point.x >= distribution_visualization.confidence_bounds.lower && 
                 point.x <= distribution_visualization.confidence_bounds.upper
    }));

    return (
      <div className="bg-white rounded-lg p-6 shadow-lg">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <TrendingUp className="mr-2 h-5 w-5 text-blue-600" />
          Your Score on the Population Distribution
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="x" 
                domain={[-3, 3]}
                type="number"
                tickFormatter={(value) => value.toFixed(1)}
              />
              <YAxis hide />
              <Tooltip 
                formatter={(value, name) => [value.toFixed(4), 'Probability Density']}
                labelFormatter={(value) => `Score: ${value.toFixed(2)}`}
              />
              <Area 
                type="monotone" 
                dataKey="y" 
                stroke="#3b82f6" 
                fill="#bfdbfe" 
                fillOpacity={0.6}
              />
              <ReferenceLine 
                x={distribution_visualization.user_score} 
                stroke="#dc2626" 
                strokeWidth={3}
                label={{ value: "Your Score", position: "top" }}
              />
              <ReferenceLine 
                x={distribution_visualization.confidence_bounds.lower} 
                stroke="#f97316" 
                strokeDasharray="5 5"
              />
              <ReferenceLine 
                x={distribution_visualization.confidence_bounds.upper} 
                stroke="#f97316" 
                strokeDasharray="5 5"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <div className="flex items-center justify-between">
            <span>Low Social Anxiety</span>
            <span>High Social Anxiety</span>
          </div>
          <div className="mt-2 text-center">
            <span className="inline-block w-3 h-3 bg-red-600 mr-2"></span>
            Your Score: {assessment_summary.theta_score}
            <span className="ml-4 inline-block w-3 h-3 bg-orange-400 mr-2"></span>
            95% Confidence Range
          </div>
        </div>
      </div>
    );
  };

  const CrossWalkScoresTab = () => {
    const userTheta = assessment_summary.theta_score;
    
    const crossWalkResults = Object.entries(crossWalkData).map(([scaleKey, scaleInfo]) => {
      const result = findCrossWalkScore(userTheta, scaleKey);
      return {
        key: scaleKey,
        name: scaleInfo.name,
        items: scaleInfo.items,
        maxScore: scaleInfo.maxScore,
        predictedScore: result ? result.score : 'N/A',
        percentage: result ? ((result.score / scaleInfo.maxScore) * 100).toFixed(1) : 'N/A'
      };
    });

    return (
      <div className="bg-white rounded-lg p-6 shadow-lg">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Scale className="mr-2 h-5 w-5 text-blue-600" />
          Predicted Scores on Other Social Anxiety Measures
        </h3>
        <p className="text-gray-600 mb-6">
          Based on your SAD-CAT theta score of {userTheta}, here are your predicted scores on other validated social anxiety scales:
        </p>
        
        <div className="grid gap-4">
          {crossWalkResults.map((scale) => (
            <div key={scale.key} className="border rounded-lg p-4 hover:bg-gray-50">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-800">{scale.name}</h4>
                  <p className="text-sm text-gray-600">{scale.items} items • Score range: 0-{scale.maxScore}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">{scale.predictedScore}</div>
                  <div className="text-sm text-gray-500">({scale.percentage}%)</div>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${scale.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-semibold text-blue-800 mb-2">About Cross-Walk Scores</h4>
          <p className="text-sm text-blue-700">
            These predicted scores are based on IRT-based equating procedures that map your SAD-CAT theta score 
            to equivalent sum scores on other social anxiety measures. The predictions provide an estimate of how 
            you might score on these scales if you completed them, based on your current level of social anxiety.
          </p>
        </div>
      </div>
    );
  };

  const DetailedItemPredictions = () => {
    return (
      <div className="bg-white rounded-lg p-6 shadow-lg">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <BarChart3 className="mr-2 h-5 w-5 text-blue-600" />
          Predicted Responses to All 26 Items
        </h3>
        <p className="text-gray-600 mb-6">
          Based on your theta score, here are your predicted responses to all items in the SAD-CAT item bank:
        </p>
        
        <div className="space-y-4">
          {predicted_responses.all_predictions.map((prediction, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-800 mb-1">
                    Item {prediction.item_id + 1}
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    {prediction.question}
                  </p>
                </div>
                <div className="text-right ml-4">
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                    {prediction.predicted_response}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    {(prediction.confidence * 100).toFixed(1)}% confidence
                  </p>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="text-xs text-gray-500 mb-1">Response Probabilities:</div>
                {Object.entries(prediction.probabilities).map(([response, prob]) => (
                  <div key={response} className="flex items-center">
                    <span className="w-16 text-xs text-gray-600">{response}</span>
                    <div className="flex-1 mx-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${prob * 100}%` }}
                        ></div>
                      </div>
                    </div>
                    <span className="w-12 text-xs text-gray-500 text-right">
                      {(prob * 100).toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Your SAD-CAT Results
          </h1>
          <p className="text-gray-600">
            Comprehensive analysis of your social anxiety assessment
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          
          {/* Score Card */}
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Your Score</h3>
                <p className="text-3xl font-bold text-blue-600">
                  {assessment_summary.theta_score}
                </p>
                <p className="text-sm text-gray-600">
                  ± {assessment_summary.standard_error} (SE)
                </p>
              </div>
              <Target className="h-12 w-12 text-blue-600" />
            </div>
          </div>

          {/* Percentile Card */}
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Percentile Rank</h3>
                <p className="text-3xl font-bold text-green-600">
                  {percentile_feedback.percentile_ordinal}
                </p>
                <p className="text-sm text-gray-600">
                  ({percentile_feedback.confidence_interval.lower_percentile.toFixed(1)}% - {percentile_feedback.confidence_interval.upper_percentile.toFixed(1)}%)
                </p>
              </div>
              <Users className="h-12 w-12 text-green-600" />
            </div>
          </div>

          {/* Severity Card */}
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Severity Level</h3>
                <p 
                  className="text-3xl font-bold"
                  style={{ color: severity_assessment.color }}
                >
                  {severity_assessment.level}
                </p>
                <p className="text-sm text-gray-600">
                  {assessment_summary.items_completed} items completed
                </p>
              </div>
              <Brain className="h-12 w-12" style={{ color: severity_assessment.color }} />
            </div>
          </div>
        </div>

        {/* Detailed Explanation */}
        <div className="bg-white rounded-lg p-6 shadow-lg mb-8">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <CheckCircle className="mr-2 h-5 w-5 text-green-600" />
            What Your Results Mean
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Percentile Interpretation</h4>
              <p className="text-gray-700 mb-4">
                Your social anxiety score places you in the <strong>{percentile_feedback.percentile_ordinal} percentile</strong> based 
                on a community sample. This means you scored higher than {percentile_feedback.percentile.toFixed(1)}% of people 
                in the general population.
              </p>
              <p className="text-gray-600 text-sm">
                We are 95% confident that your true score lies between the <strong>
                {percentile_feedback.confidence_interval.lower_percentile.toFixed(1)}th</strong> and <strong>
                {percentile_feedback.confidence_interval.upper_percentile.toFixed(1)}th</strong> percentiles.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Clinical Interpretation</h4>
              <p className="text-gray-700 mb-4">
                {severity_assessment.description}
              </p>
              <div className="space-y-2">
                <h5 className="font-medium text-gray-800">Recommendations:</h5>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  {severity_assessment.recommendations.map((rec, index) => (
                    <li key={index}>{rec}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Score Distribution
              </button>
              <button
                onClick={() => setActiveTab('crosswalk')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'crosswalk'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Cross-walk Scores
              </button>
              <button
                onClick={() => setActiveTab('detailed')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'detailed'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                All Item Predictions
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="mb-8">
          {activeTab === 'overview' && <ScoreDistributionChart />}
          {activeTab === 'crosswalk' && <CrossWalkScoresTab />}
          {activeTab === 'detailed' && <DetailedItemPredictions />}
        </div>

        {/* Action Buttons */}
        <div className="text-center space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={downloadPDF}
              disabled={pdfLoading}
              className={`${
                pdfLoading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700'
              } text-white px-8 py-3 rounded-lg transition-colors flex items-center gap-2`}
            >
              {pdfLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Generating PDF...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Comprehensive Report (PDF)
                </>
              )}
            </button>
            
            <button
              onClick={onStartNew}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Start New Assessment
            </button>
          </div>
          
          <p className="text-sm text-gray-600">
            The PDF report includes your scores, cross-walk comparisons, and detailed item analysis.
          </p>
        </div>

      </div>
    </div>
  );
};

export default EnhancedResultsPage;